import { registerActions } from "../actionRegistry";
import { requestAiMetadata } from "./aiMetadataLive";
// Local upload tracking
let mr_total_uploads = 0;
import { modal } from "../ui/modal";
import { toastError } from "../ui/messages";
import {
  getDomElement,
  delegate,
  setText,
  setStyle,
  hide,
  fadeIn,
  prependHtml,
} from "../utils/dom";
import { hashCode, perfectround } from "../utils/string";
import { api } from "../api";
import { optinartwork } from "./miscOperations";
import { mountDirectoryCard } from "./components/DirectoryArtworkCard";
import { renderDirectoryEditTable } from "./components/DirectoryEditTable";

// Delegation for dynamically inserted directory artwork cards
// (the rest of this file still uses jQuery for the kept dropzone shim)
delegate("click", "[data-dir-edit]", (_e, el) => {
  modal(`directoryedit:${el.getAttribute("data-dir-edit") ?? ""}:`, "large");
});
delegate("click", "[data-optin]", (_e, el) => {
  void optinartwork(el.getAttribute("data-optin") ?? "");
});
// focusout, not blur: native blur doesn't bubble to the document
delegate("focusout", "[data-dir-update-blur]", (_e, el) => {
  const id = el.getAttribute("data-dir-update-blur") ?? "";
  const field = el.getAttribute("data-field") ?? "";
  void directoryUpdate(id, field, (el as HTMLInputElement).value);
});
delegate("change", "[data-dir-update-change]", (_e, el) => {
  const id = el.getAttribute("data-dir-update-change") ?? "";
  const field = el.getAttribute("data-field") ?? "";
  void directoryUpdate(id, field, (el as HTMLSelectElement).value);
});
export function directoryUploadStarted(file: { name: string }): void {
  hide("directory-upload-empty");
  const id = String(hashCode(file.name));
  // The duplicate guard checks the id this function EMITS. It used to test
  // `slot-uploading-<id>`, which nothing in either app has ever emitted — so it
  // was unconditionally true and the guard had never once prevented a duplicate
  // tile. (`slot-uploading-` is carried in check-dom-ids' KNOWN_DEAD for exactly
  // this reason.) Note the remaining hazard is upstream: `id` is a hash of the
  // FILENAME, so re-uploading the same name legitimately collides — see the
  // note in features/uploadCallbacks.ts.
  if (getDomElement("artwork-upload-" + id) == null) {
    prependHtml(
      "directory-artworks",
      '<div class="directory-artwork" id="artwork-upload-' +
        id +
        '"><div class="directory-artwork-img"><div class="directory-artwork-image" id="artwork-upload-img-' +
        id +
        '"></div></div><div id="artwork-upload-status-' +
        id +
        '" class="directory-artwork-status"><div class="directory-artwork-status-title">---</div>Preparing upload...</div><div class="directory-artwork-title"></div><div class="directory-artwork-category"></div><div class="directory-artwork-listing"></div><div class="directory-artwork-score"></div><div class="directory-artwork-buttons"><span class="button medium lilac" style="opacity: .3;">Edit</span></div></div>'
    );
  }
}

export function directoryUploadProgress(
  file: { name: string },
  progress: number,
  _bytesSent: number
): void {
  const id = String(hashCode(file.name));
  if (getDomElement("artwork-upload-" + id) != null) {
    // `setText`, not `setHtml`: the status line is a filename plus a number, and a
    // filename is chosen by whoever supplies the file.
    setText(
      "artwork-upload-status-" + id,
      file.name + " uploading... " + String(perfectround(progress)) + "%"
    );
  }
}

/**
 * Called after a presigned S3 PUT succeeds. The S3 response body is empty, so we
 * ignore it and ask the PHP backend to run the resize Lambda + persist metadata,
 * then render the finished artwork card from the returned dbreg.
 */
export async function directoryUploadS3Done(file: DropzoneFile): Promise<void> {
  const id = String(hashCode(file.name));
  setText("artwork-upload-status-" + id, file.name + " processing...");

  let dbreg: string;
  try {
    const result = await api.artwork.directoryUploadComplete({
      uuid: file.uploadUuid ?? "",
      filename: file.name,
    });
    dbreg = String(result.dbreg);
  } catch (err) {
    const text = (err as Error).message || "Processing failed. Try again later.";
    setText("artwork-upload-status-" + id, text);
    return;
  }

  // Kick off AI metadata as soon as the file is in S3 and has an Artwork row. Fire and
  // forget: this is a multi-second Gemini call and a directory upload can be hundreds of
  // files, so awaiting it here would serialise the whole batch behind the model.
  // requestAiMetadata caches the answer, so an edit screen opened later still gets it.
  requestAiMetadata(Number(dbreg));

  if (getDomElement("artwork-upload-" + id) != null) {
    getDomElement("artwork-upload-" + id)?.remove();
    prependHtml(
      "directory-artworks",
      '<div class="directory-artwork" id="artwork-' + dbreg + '" style="display: none;"></div>'
    );
    directoryRefresh(dbreg).catch((err: unknown) => {
      toastError((err as Error).message);
    });
    // "" clears the inline display rather than setting one, so the stylesheet decides:
    // `inline-block` in thumbnail view, `block` in list view. fadeIn's default of "block"
    // is an INLINE style and therefore beats `div.directory-artworks-thumbs
    // div.directory-artwork { display: inline-block }` — which made every freshly uploaded
    // artwork a full-width block sitting on its own line instead of joining the grid. The bug
    // was invisible in list view, where the tiles are `display: block` anyway.
    fadeIn("artwork-" + dbreg, 400, "");
  }
}

/**
 * Display an upload error in the placeholder card's status box. Used for both
 * client-side rejections (non-JPEG) and failed presign / S3 PUT requests.
 */
export function directoryUploadError(
  file: DropzoneFile,
  message: string | { error?: string }
): void {
  const id = String(hashCode(file.name));
  const text = typeof message === "string" ? message : (message?.error ?? "Upload failed");
  setText("artwork-upload-status-" + id, text);
}

export function directoryUploadThumb(file: { name: string }, dataUrl: string): void {
  const id = String(hashCode(file.name));
  if (getDomElement("artwork-upload-img-" + id) != null) {
    setStyle("artwork-upload-img-" + id, "background-image", "url(" + dataUrl + ")");
  }
}

export async function directoryRefresh(id: string | number): Promise<void> {
  const idStr = String(id);
  const container = getDomElement("artwork-" + idStr);
  if (container) {
    const response = await api.artwork.directoryRefresh({ id: Number(id) });
    if (container.dataset.preactManaged) {
      window.dispatchEvent(
        new CustomEvent("artwork-refreshed", { detail: { artwork: response.artwork } })
      );
    } else {
      mountDirectoryCard(
        container,
        response.artwork,
        response.categories,
        (artId, field, value) => {
          void directoryUpdate(artId, field, value);
        }
      );
    }
  }
}

export async function directoryRefreshAlt(id: string | number): Promise<void> {
  const idStr = String(id);
  const container = getDomElement("artwork-" + idStr);
  if (container) {
    const response = await api.artwork.directoryRefresh({ id: Number(id) });
    mountDirectoryCard(container, response.artwork, response.categories, (artId, field, value) => {
      void directoryUpdateAlt(artId, field, value);
    });
  }
}

type DirectoryField = "artist_name" | "title" | "category" | "status" | "tags";

export async function directoryUpdate(
  id: string | number,
  field: string,
  value: string
): Promise<void> {
  try {
    await api.artwork.directoryUpdate({ id: Number(id), field: field as DirectoryField, value });
    if (getDomElement("edit_artwork") == null) {
      await directoryRefresh(id);
    } else {
      await directoryEditRefresh(id);
    }
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function directoryUpdateAlt(
  id: string | number,
  field: string,
  value: string
): Promise<void> {
  try {
    await api.artwork.directoryUpdate({ id: Number(id), field: field as DirectoryField, value });
    if (getDomElement("edit_artwork") == null) {
      await directoryRefreshAlt(id);
    } else {
      await directoryEditRefreshAlt(id);
    }
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function directoryEditRefresh(id: string | number): Promise<void> {
  const idStr = String(id);
  if (getDomElement("directory-edit-" + idStr) != null) {
    const response = await api.artwork.directoryEditRefresh({ id: Number(id) });
    // The route returns {} when the artwork isn't visible to this user. (The old
    // `status == "OK"` check compared against a field the raft route never returns,
    // so this refresh silently never ran.)
    // Narrow on the value, not `"score" in response`: under exactOptionalPropertyTypes
    // the empty branch types as `{ score?: never }`, which `in` does not eliminate.
    if (response.score !== undefined) {
      renderDirectoryEditTable(idStr, response);
      if (getDomElement("artwork-" + idStr) != null) {
        await directoryRefresh(id);
      }
    }
  }
}

export async function directoryEditRefreshAlt(id: string | number): Promise<void> {
  const idStr = String(id);
  if (getDomElement("directory-edit-" + idStr) != null) {
    const response = await api.artwork.directoryEditRefresh({ id: Number(id) });
    if (response.score !== undefined) {
      renderDirectoryEditTable(idStr, response);
      if (getDomElement("artwork-" + idStr) != null) {
        await directoryRefreshAlt(id);
      }
    }
  }
}

export function mrUploadStarted(file: { name: string }): void {
  hide("no-current-uploads");
  const id = String(hashCode(file.name));
  if (getDomElement("massupload-" + id) == null) {
    if (typeof mr_total_uploads !== "undefined") {
      mr_total_uploads = mr_total_uploads + 1;
    }
    prependHtml(
      "uploads",
      '<div id="massupload-' +
        id +
        '" class="mr-upload"><span class="mr-upload-img"><i class="far fa-file-image"></i></span> <span class="mr-upload-title">' +
        file.name +
        '</span> <span class="mr-upload-progress" id="massupload-progress-' +
        id +
        '">0%</span></div>'
    );
  }
}

export function mrUploadProgress(
  file: { name: string },
  progress: number,
  _bytesSent: number
): void {
  const id = String(hashCode(file.name));
  if (getDomElement("massupload-progress-" + id) != null) {
    setText("massupload-progress-" + id, String(perfectround(progress)) + "%");
  }
}

registerActions({
  directoryRefreshAlt,
  directoryEditRefreshAlt,
  mrUploadStarted,
  mrUploadProgress,
});
